import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PracticeMark } from '../../components/AppHeader.tsx';
import { LogoCropper } from './LogoCropper.tsx';
import { PracticeAdminHead } from './PracticeAdmin.tsx';
import { usePracticeAdmin } from './practiceAdminContext.ts';
import { useAuthFetch } from '@/hooks';
import { BRAND_COLOR_PRESETS, DEFAULT_BRAND_COLOR, convertHeicIfNeeded, legibleBrandColor, practiceThemeTokens, resizeImageIfNeeded } from '@/utils';
import { practicePath } from '../../routes.ts';
import { ALLOWED_IMAGE_TYPES } from '@dermalink/shared';
import type {
  OrgAdminBackdropUploadUrlResponse,
  OrgAdminOrganization,
  OrgAdminOrganizationListResponse,
  OrgAdminSetBackdropResponse,
  OrgAdminSetBrandColorResponse,
  OrgAdminSetLogoResponse,
  PracticeLogo,
} from '@dermalink/shared';
import styles from './PracticeSettings.module.css';
import admin from './PracticeAdmin.module.css';

// The presigned POST rejects anything over 10 MB (apps/api/src/lib/s3.ts). Checked
// after the client-side resize, since phone photos are usually larger before it.
const MAX_FILE_SIZE = 10 * 1024 * 1024;

export function PracticeSettings() {
  const { org } = usePracticeAdmin();
  return (
    <div className={admin.container}>
      <div className={`${admin.inner} ${admin.innerNarrow}`}>
        <PracticeAdminHead title="Huisstijl" />
        <LogoCard key={`logo-${org.id}`} org={org} />
        <BrandColorCard key={`color-${org.id}`} org={org} />
        <BackdropCard key={org.id} org={org} />
        <DeeplinkCard org={org} />
      </div>
    </div>
  );
}

function BackdropCard({ org }: { org: OrgAdminOrganization }) {
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const fileRef     = useRef<HTMLInputElement>(null);
  const [busy, setBusy]   = useState<'upload' | 'remove' | null>(null);
  const [error, setError] = useState<string | null>(null);

  function applyBackdropUrl(backdrop_url: string | null) {
    queryClient.setQueryData<OrgAdminOrganizationListResponse>(['org-admin-organizations'], (old) =>
      old ? { organizations: old.organizations.map((o) => o.id === org.id ? { ...o, backdrop_url } : o) } : old,
    );
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    let file = e.target.files?.[0];
    if (!fileRef.current) return;
    fileRef.current.value = '';
    if (!file) return;

    // Windows/Android report an empty type for HEIC/HEIF — see DoctorProfile.
    if (!file.type) {
      const ext = file.name.split('.').pop()?.toLowerCase();
      const mimeType = ext === 'heic' ? 'image/heic' : ext === 'heif' ? 'image/heif' : file.type;
      if (mimeType !== file.type) file = new File([file], file.name, { type: mimeType });
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type as typeof ALLOWED_IMAGE_TYPES[number])) {
      setError('Ongeldig bestandstype. Gebruik JPEG, PNG of WebP.');
      return;
    }
    setError(null);
    setBusy('upload');
    try {
      file = await convertHeicIfNeeded(file);
      file = await resizeImageIfNeeded(file);
      if (file.size > MAX_FILE_SIZE) throw new Error('Bestand is te groot (max 10 MB).');

      const urlRes = await authFetch(`/org-admin/organizations/${org.id}/backdrop-upload-url`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ filename: file.name, mime_type: file.type }),
      });
      if (!urlRes.ok) throw new Error('Kon geen upload-URL ophalen.');
      const { url, fields, s3_key } = await urlRes.json() as OrgAdminBackdropUploadUrlResponse;

      const formData = new FormData();
      for (const [k, v] of Object.entries(fields)) formData.append(k, v);
      formData.append('file', file);
      const postRes = await fetch(url, { method: 'POST', body: formData });
      if (!postRes.ok) throw new Error('Upload mislukt.');

      const saveRes = await authFetch(`/org-admin/organizations/${org.id}/backdrop`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ s3_key, mime_type: file.type }),
      });
      if (!saveRes.ok) throw new Error('Opslaan mislukt.');
      applyBackdropUrl((await saveRes.json() as OrgAdminSetBackdropResponse).backdrop_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(null);
    }
  }

  async function handleRemove() {
    setError(null);
    setBusy('remove');
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/backdrop`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt.');
      applyBackdropUrl(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(null);
    }
  }

  const previewStyle = org.backdrop_url
    ? { '--_backdrop-image': `url("${org.backdrop_url}")` } as React.CSSProperties
    : undefined;

  return (
    <div className={styles.card}>
      <p className={styles.sectionLabel}>Achtergrond</p>
      <p className={styles.hint}>Deze foto verschijnt (vervaagd) achter de patiëntenpagina's wanneer patiënten via de link van {org.name} binnenkomen, en bij hun consulten met {org.name}.</p>
      <div className={styles.preview} style={previewStyle}>
        {!org.backdrop_url && <span className={styles.previewBadge}>Standaardachtergrond</span>}
      </div>
      <div className={styles.actions}>
        {org.backdrop_url && (
          <button type="button" className={styles.uploadBtn} onClick={() => { void handleRemove(); }} disabled={busy !== null}>
            {busy === 'remove' ? 'Verwijderen…' : 'Standaard herstellen'}
          </button>
        )}
        <label className={styles.uploadBtn}>
          {busy === 'upload' ? 'Uploaden…' : org.backdrop_url ? 'Foto wijzigen' : 'Foto uploaden'}
          <input ref={fileRef} type="file" accept={ALLOWED_IMAGE_TYPES.join(',')} onChange={(e) => { void handleFileChange(e); }} disabled={busy !== null} />
        </label>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

// Logos are framed and rendered to PNG in the browser, so only formats it can decode.
const LOGO_SOURCE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

function LogoCard({ org }: { org: OrgAdminOrganization }) {
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  const [file, setFile]   = useState<File | null>(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  function applyLogo(logo: PracticeLogo | null) {
    queryClient.setQueryData<OrgAdminOrganizationListResponse>(['org-admin-organizations'], (old) =>
      old ? { organizations: old.organizations.map((o) => o.id === org.id ? { ...o, logo } : o) } : old,
    );
    void queryClient.invalidateQueries({ queryKey: ['public-organization', org.slug] });
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (!LOGO_SOURCE_TYPES.includes(picked.type)) {
      setError('Gebruik een PNG, JPEG of WebP. Een PNG met transparante achtergrond werkt het best.');
      return;
    }
    if (picked.size > MAX_FILE_SIZE) {
      setError('Bestand is te groot (max 10 MB).');
      return;
    }
    setError(null);
    setFile(picked);
  }

  async function handleConfirm(png: Blob) {
    setError(null);
    setBusy(true);
    try {
      const urlRes = await authFetch(`/org-admin/organizations/${org.id}/logo-upload-url`, { method: 'POST' });
      if (!urlRes.ok) throw new Error('Kon geen upload-URL ophalen.');
      const { url, fields, s3_key } = await urlRes.json() as OrgAdminBackdropUploadUrlResponse;

      const formData = new FormData();
      for (const [k, v] of Object.entries(fields)) formData.append(k, v);
      formData.append('file', png, 'logo.png');
      const postRes = await fetch(url, { method: 'POST', body: formData });
      if (!postRes.ok) throw new Error('Upload mislukt.');

      const saveRes = await authFetch(`/org-admin/organizations/${org.id}/logo`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ s3_key }),
      });
      if (!saveRes.ok) throw new Error('Opslaan mislukt.');
      applyLogo((await saveRes.json() as OrgAdminSetLogoResponse).logo);
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    setError(null);
    setBusy(true);
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/logo`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Verwijderen mislukt.');
      applyLogo(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.card}>
      <p className={styles.sectionLabel}>Logo</p>
      <p className={styles.hint}>Verschijnt bovenaan de patiëntenpagina's van {org.name}, naast de naam van de praktijk.</p>
      {file ? (
        <LogoCropper file={file} practiceName={org.name} busy={busy} onCancel={() => setFile(null)} onConfirm={(png) => { void handleConfirm(png); }} />
      ) : (
        <>
          <div className={styles.themePreviewHeader}>
            <PracticeMark practice={org} />
          </div>
          <div className={styles.actions}>
            {org.logo && (
              <button type="button" className={styles.uploadBtn} onClick={() => { void handleRemove(); }} disabled={busy}>
                {busy ? 'Verwijderen…' : 'Logo verwijderen'}
              </button>
            )}
            <label className={styles.uploadBtn}>
              {org.logo ? 'Logo wijzigen' : 'Logo uploaden'}
              <input type="file" accept={LOGO_SOURCE_TYPES.join(',')} onChange={handleFileChange} disabled={busy} />
            </label>
          </div>
        </>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

function BrandColorCard({ org }: { org: OrgAdminOrganization }) {
  const authFetch   = useAuthFetch();
  const queryClient = useQueryClient();
  // null = the Dermalink default.
  const [draft, setDraft] = useState<string | null>(org.brand_color);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shown    = draft ?? DEFAULT_BRAND_COLOR;
  const adjusted = legibleBrandColor(shown) !== shown;

  async function save(brand_color: string | null) {
    setError(null);
    setBusy(true);
    try {
      const res = await authFetch(`/org-admin/organizations/${org.id}/brand-color`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ brand_color }),
      });
      if (!res.ok) throw new Error('Opslaan mislukt.');
      const saved = (await res.json() as OrgAdminSetBrandColorResponse).brand_color;
      setDraft(saved);
      queryClient.setQueryData<OrgAdminOrganizationListResponse>(['org-admin-organizations'], (old) =>
        old ? { organizations: old.organizations.map((o) => o.id === org.id ? { ...o, brand_color: saved } : o) } : old,
      );
      void queryClient.invalidateQueries({ queryKey: ['public-organization', org.slug] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Er is een fout opgetreden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.card}>
      <p className={styles.sectionLabel}>Huisstijl</p>
      <p className={styles.hint}>De kleur van knoppen en accenten op de patiëntenpagina's van {org.name}. Dermalink blijft vermeld als platform.</p>
      <div className={styles.swatches} role="radiogroup" aria-label="Kleur">
        {BRAND_COLOR_PRESETS.map((preset) => {
          const value    = preset.color === DEFAULT_BRAND_COLOR ? null : preset.color;
          const selected = draft === value;
          return (
            <button key={preset.name} type="button" role="radio" aria-checked={selected} className={`${styles.swatch}${selected ? ` ${styles.swatchSelected}` : ''}`} onClick={() => setDraft(value)}>
              <span className={styles.swatchDot} style={{ background: preset.color }} />
              {preset.name}
            </button>
          );
        })}
        <label className={`${styles.swatch}${draft && !BRAND_COLOR_PRESETS.some((p) => p.color === draft) ? ` ${styles.swatchSelected}` : ''}`}>
          <input type="color" className={styles.swatchPicker} value={shown} onChange={(e) => setDraft(e.target.value.toLowerCase())} />
          Eigen kleur
        </label>
      </div>
      <div className={styles.themePreview} style={draft ? practiceThemeTokens(draft) as React.CSSProperties : undefined}>
        <div className={styles.themePreviewHeader}>
          <PracticeMark practice={org} />
        </div>
        <div className={styles.themePreviewBody}>
          <div className="primary-tint-card">Uw consult is ontvangen. De dermatoloog antwoordt binnen 5 werkdagen.</div>
          <span className="btn btn-primary">Teleconsult starten</span>
        </div>
      </div>
      {adjusted && <p className={styles.hint}>Deze kleur is iets donkerder gemaakt zodat witte tekst erop leesbaar blijft.</p>}
      <div className={styles.actions}>
        {org.brand_color && (
          <button type="button" className={styles.uploadBtn} onClick={() => { void save(null); }} disabled={busy}>Standaard herstellen</button>
        )}
        <button type="button" className={styles.uploadBtn} onClick={() => { void save(draft); }} disabled={busy || draft === org.brand_color}>
          {busy ? 'Opslaan…' : 'Opslaan'}
        </button>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

function DeeplinkCard({ org }: { org: OrgAdminOrganization }) {
  const [copied, setCopied] = useState(false);
  const path     = practicePath(org.slug);
  const deeplink = `${window.location.origin}${path}`;

  async function handleCopy() {
    await navigator.clipboard.writeText(deeplink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className={styles.card}>
      <p className={styles.sectionLabel}>Link voor patiënten</p>
      <p className={styles.hint}>Plaats deze link op de website van de praktijk.</p>
      <div className={styles.linkRow}>
        <a href={deeplink} target="_blank" rel="noopener noreferrer">{deeplink}</a>
        <button type="button" className={styles.uploadBtn} onClick={() => { void handleCopy(); }}>{copied ? 'Gekopieerd' : 'Kopieer'}</button>
      </div>
    </div>
  );
}
